import { createSlice } from '@reduxjs/toolkit'
import { keyBy } from '../../../helpers/common'
import { register } from '../../../helpers/reducerRegistry'
import { SORT_DIRECTION } from '../../../utils/common'
import { OTHER_ID } from '../ownershipStructure/constants'
import {
  getHighlights,
  getOwnershipDetail,
  getOwnershipHistorical,
  getOwnershipTypes,
  getRelatedCompanyViewBy,
} from './thunk'

export const keyLoading = {
  HIGHLIGHTS: 'highlights',
  DETAIL: 'detail',
  VIEW_BY: 'viewBy',
  DETAIL_HISTORY: 'detailHistory',
}

const initialState = {
  loading: {
    [keyLoading.HIGHLIGHTS]: true,
    [keyLoading.DETAIL]: true,
    [keyLoading.VIEW_BY]: true,
    [keyLoading.DETAIL_HISTORY]: true,
  },
  highlights: {},
  ownershipTypes: [],

  ownershipStructureIds: [],
  ownershipStructureData: {},
  filterTable: {},
  shareHolderActiveIndex: undefined,
  ownershipTypeActiveIndex: undefined,

  parentCompanyIds: [],
  parentCompanyData: {},

  branchIds: [],
  branchData: {},

  subsidiaryIds: [],
  subsidiaryData: {},

  associateCompanyIds: [],
  associateCompanyData: {},

  relatedCompanyIds: [],
  relatedCompanyData: {},

  ownershipStructureHistoryIds: [],
  ownershipStructureHistoryData: {},
  ownershipStructureHistoryFilter: {
    sortBy: 'publicDateId',
    sortDirection: SORT_DIRECTION.DESC,
  },
}

export const slice = createSlice({
  name: 'companyAnalysis/ownershipAndGroup',
  initialState,
  reducers: {
    resetStore: (state) => {
      state.loading = {
        [keyLoading.HIGHLIGHTS]: true,
        [keyLoading.DETAIL]: true,
        [keyLoading.VIEW_BY]: true,
        [keyLoading.DETAIL_HISTORY]: true,
      }
      state.highlights = {}
      state.ownershipTypes = []
      state.ownershipStructureIds = []
      state.ownershipStructureData = {}
      state.filterTable = {}
      state.parentCompanyIds = []
      state.parentCompanyData = {}
      state.branchIds = []
      state.branchData = {}
      state.subsidiaryIds = []
      state.subsidiaryData = {}
      state.associateCompanyIds = []
      state.associateCompanyData = {}
      state.relatedCompanyIds = []
      state.relatedCompanyData = {}
      state.ownershipStructureHistoryIds = []
      state.ownershipStructureHistoryData = {}
    },
    filterTable: (state, { payload }) => {
      let ids = Object.keys(state.ownershipStructureData)
      Object.values(payload).forEach((item) => {
        if (item.key) {
          if (item[item.key] === OTHER_ID) {
            ids = ids.filter(
              (id) =>
                !item.exceptIds.includes(
                  state.ownershipStructureData[id][item.key],
                ),
            )
          } else {
            ids = ids.filter(
              (id) =>
                state.ownershipStructureData[id][item.key] === item[item.key],
            )
          }
        }
      })

      state.ownershipStructureIds = ids
    },
    changeShareHolderActiveIndex: (state, action) => {
      state.shareHolderActiveIndex = action.payload
      state.ownershipTypeActiveIndex = undefined
    },
    changeOwnershipTypeActiveIndex: (state, action) => {
      state.ownershipTypeActiveIndex = action.payload
      state.shareHolderActiveIndex = undefined
    },
    changeOwnershipStructureFilter: (state, action) => {
      state.ownershipStructureHistoryFilter = {
        ...state.ownershipStructureHistoryFilter,
        ...action.payload,
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getHighlights.pending, (state) => {
      state.loading[keyLoading.HIGHLIGHTS] = true
      state.highlights = {}
    })
    builder.addCase(getHighlights.fulfilled, (state, { payload = {} }) => {
      state.loading[keyLoading.HIGHLIGHTS] = false
      state.highlights = payload
    })
    builder.addCase(getHighlights.rejected, (state, action) => {
      state.loading[keyLoading.HIGHLIGHTS] = action.payload.isLoading
    })

    builder.addCase(getOwnershipDetail.pending, (state) => {
      state.loading[keyLoading.DETAIL] = true

      state.ownershipStructureIds = []
      state.ownershipStructureData = {}
      state.parentCompanyIds = []
      state.parentCompanyData = {}
      state.branchIds = []
      state.branchData = {}
      state.subsidiaryIds = []
      state.subsidiaryData = {}
      state.associateCompanyIds = []
      state.associateCompanyData = {}
    })
    builder.addCase(getOwnershipDetail.fulfilled, (state, { payload = {} }) => {
      state.loading[keyLoading.DETAIL] = false

      const {
        structure = [],
        parrent,
        branches = [],
        subsidiaries = [],
        associatedCompany = [],
      } = payload

      const structureWithId = structure.map((item, index) => ({
        ...item,
        id: index + 1,
        ownershipTypeId: item.ownershipTypeToChartId,
      }))
      const structureIds = structureWithId.map((item) => item.id)
      state.ownershipStructureIds = structureIds
      state.ownershipStructureData = keyBy(structureWithId, 'id')

      if (parrent) {
        state.parentCompanyIds = [1]
        state.parentCompanyData = { 1: { ...parrent, id: 1 } }
      }

      const branchWithId = branches.map((item, index) => ({
        ...item,
        id: index + 1,
      }))
      state.branchIds = branchWithId.map((item) => item.id)
      state.branchData = keyBy(branchWithId, 'id')

      const subsidiaryWithId = subsidiaries.map((item, index) => ({
        ...item,
        id: index + 1,
      }))
      state.subsidiaryIds = subsidiaryWithId.map((item) => item.id)
      state.subsidiaryData = keyBy(subsidiaryWithId, 'id')

      const associateCompanyWithId = associatedCompany.map((item, index) => ({
        ...item,
        id: index + 1,
      }))
      state.associateCompanyIds = associateCompanyWithId.map((item) => item.id)
      state.associateCompanyData = keyBy(associateCompanyWithId, 'id')
    })
    builder.addCase(getOwnershipDetail.rejected, (state, action) => {
      state.loading[keyLoading.DETAIL] = action.payload.isLoading
    })

    builder.addCase(getOwnershipHistorical.pending, (state) => {
      state.loading[keyLoading.DETAIL_HISTORY] = true
      state.ownershipStructureHistoryIds = []
      state.ownershipStructureHistoryData = {}
    })
    builder.addCase(
      getOwnershipHistorical.fulfilled,
      (state, { payload = {} }) => {
        state.loading[keyLoading.DETAIL_HISTORY] = false

        const structureWithId = payload.map((item, index) => ({
          ...item,
          id: index + 1,
          ownershipTypeId: item.ownershipTypeToChartId,
        }))
        const structureIds = structureWithId.map((item) => item.id)
        state.ownershipStructureHistoryIds = structureIds
        state.ownershipStructureHistoryData = keyBy(structureWithId, 'id')
      },
    )
    builder.addCase(getOwnershipHistorical.rejected, (state, action) => {
      state.loading[keyLoading.DETAIL_HISTORY] = action.payload.isLoading
    })

    builder.addCase(getRelatedCompanyViewBy.pending, (state) => {
      state.loading[keyLoading.VIEW_BY] = true
      state.relatedCompanyIds = []
      state.relatedCompanyData = {}
    })
    builder.addCase(
      getRelatedCompanyViewBy.fulfilled,
      (state, { payload = {} }) => {
        const { relatedCompanies = [] } = payload

        const relatedCompaniesWithId = relatedCompanies.map((item, index) => ({
          ...item,
          id: index + 1,
          status: { status: item.activeStatusId, label: item.activeStatusName },
        }))
        state.relatedCompanyIds = relatedCompaniesWithId.map((item) => item.id)
        state.relatedCompanyData = keyBy(relatedCompaniesWithId, 'id')
        state.loading[keyLoading.VIEW_BY] = false
      },
    )
    builder.addCase(getRelatedCompanyViewBy.rejected, (state, action) => {
      state.loading[keyLoading.VIEW_BY] = action.payload.isLoading
    })

    builder.addCase(getOwnershipTypes.pending, (state) => {
      state.ownershipTypes = []
    })
    builder.addCase(getOwnershipTypes.fulfilled, (state, { payload = [] }) => {
      state.ownershipTypes = payload
    })
  },
})

export const selectLoading = (key) => (state) => state[slice.name].loading[key]
export const selectHighlights = (state) => state[slice.name].highlights
export const selectOwnershipTypes = (state) => state[slice.name].ownershipTypes
export const selectShareHolderActiveIndex = (state) =>
  state[slice.name].shareHolderActiveIndex
export const selectOwnershipTypeActiveIndex = (state) =>
  state[slice.name].ownershipTypeActiveIndex

export const selectOwnershipStructureIds = (state) =>
  state[slice.name].ownershipStructureIds
export const selectOwnershipStructureData = (rowId, colId) => (state) =>
  state[slice.name].ownershipStructureData[rowId][colId]
export const selectOwnershipStructure = (state) =>
  state[slice.name].ownershipStructureData

export const selectOwnershipStructureHistoryIds = (state) =>
  state[slice.name].ownershipStructureHistoryIds
export const selectOwnershipStructureHistoryData = (rowId, colId) => (state) =>
  state[slice.name].ownershipStructureHistoryData[rowId][colId]
export const selectOwnershipStructureHistory = (state) =>
  state[slice.name].ownershipStructureHistoryData
export const selectOwnershipStructureHistoryFilter = (state) =>
  state[slice.name].ownershipStructureHistoryFilter

export const selectParentCompanyIds = (state) =>
  state[slice.name].parentCompanyIds
export const selectParentCompanyData = (rowId, colId) => (state) =>
  state[slice.name].parentCompanyData[rowId][colId]

export const selectBranchIds = (state) => state[slice.name].branchIds
export const selectBranchData = (rowId, colId) => (state) =>
  state[slice.name].branchData[rowId][colId]

export const selectSubsidiaryIds = (state) => state[slice.name].subsidiaryIds
export const selectSubsidiaryData = (rowId, colId) => (state) =>
  state[slice.name].subsidiaryData[rowId][colId]

export const selectAssociateCompanyIds = (state) =>
  state[slice.name].associateCompanyIds
export const selectAssociateCompanyData = (rowId, colId) => (state) =>
  state[slice.name].associateCompanyData[rowId][colId]

export const selectRelatedCompanyIds = (state) =>
  state[slice.name].relatedCompanyIds
export const selectRelatedCompanyData = (rowId, colId) => (state) =>
  state[slice.name].relatedCompanyData[rowId][colId]

export const {
  filterTable,
  resetTable,
  resetStore,
  changeShareHolderActiveIndex,
  changeOwnershipTypeActiveIndex,
  changeOwnershipStructureFilter,
} = slice.actions

register(slice.name, slice.reducer)
